'use client'

import { cn } from '@/_lib/utils'
import { useOrderFlow } from '@/app/[locale]/order/_context/order-flow-context'
import { getOrderThemeClasses } from '@/app/[locale]/order/_utils/order-flow-section-theme-classes'
import { useCartPrices } from '@cart/_hooks/use-cart-prices'
import { cartProductKey } from '@cart/_utils/cart-product-key'
import { isAllAnnualProgramFamily } from '@cart/_utils/is-all-annual-program-family'

import { CartItemDisplay } from './a-cart-item-lines'
import { CartSummarySection } from './a-cart-summary-section'

/** Desktop sticky column vs mobile grey panel above the patient form. */
export type AOrderCartSidebarPlacement = 'desktop' | 'mobileAboveForm'

type AOrderCartSidebarProps = {
	placement?: AOrderCartSidebarPlacement
}

/**
 * Order cart panel: line items + optional summary; placement controls responsive shell only.
 */
export function AOrderCartSidebar({ placement = 'desktop' }: AOrderCartSidebarProps) {
	const { cartProducts, orderFlowSectionTheme, templateCartPricing } = useOrderFlow()
	const { productPrices, reportPrice, showSummary } = useCartPrices(cartProducts)

	// True when the patient hasn't chosen an insurer yet for DEFAULT/PLUS_WITH ambulances.
	// `showFromPrefix` is set by useTemplateCartPricingSync exactly in this state.
	const insurancePending = templateCartPricing?.showFromPrefix ?? false

	if (cartProducts.length === 0) return null

	const isFamily = isAllAnnualProgramFamily(cartProducts)

	const body = (
		<>
			<p className='mb-3 text-xs font-semibold uppercase tracking-widest text-medante-purple-dark/50'>
				Zhrnutie
			</p>

			<div className='space-y-2'>
				{cartProducts.map((product, idx) => {
					const key = cartProductKey(product, idx)
					return (
						<div
							key={key}
							className={cn(idx > 0 && 'border-t border-medante-purple-dark/10 pt-2')}
						>
							<CartItemDisplay
								product={product}
								variant='sidebar'
								onPriceReport={info => reportPrice(key, info)}
							/>
						</div>
					)
				})}
			{showSummary && (
				<CartSummarySection
					productPrices={productPrices}
					variant='sidebar'
					isFamily={isFamily}
					insurancePending={insurancePending}
				/>
			)}
			</div>
		</>
	)

	const shellClass = cn(
		'rounded-2xl p-4 md:p-6',
		getOrderThemeClasses(orderFlowSectionTheme).surface
	)

	if (placement === 'mobileAboveForm') {
		return (
			<div className='lg:hidden'>
				<div className={shellClass}>{body}</div>
			</div>
		)
	}

	return (
		<aside className='w-full'>
			<div className={cn('sticky top-32', shellClass)}>{body}</div>
		</aside>
	)
}
